// Canonical accessor for the post-redesign `briefing_stages` table.
// Migration 20260506001300 moved briefing_stages to per-day rows
// (one row per (briefing_id, event_date, display_order)), so the
// "stages active on day X" filter is a single equality check on
// event_date — no array scan, no override layer.

export type BriefingStageRecord = {
  id: string;
  briefing_id: string;
  label: string;
  location: string | null;
  event_date: string; // ISO yyyy-mm-dd
  display_order: number;
};

export function stagesForDay(
  stages: BriefingStageRecord[],
  eventDate: string,
): BriefingStageRecord[] {
  return stages
    .filter((s) => s.event_date === eventDate)
    .slice()
    .sort((a, b) => a.display_order - b.display_order);
}

// The wizard's counterpart to stagesForDay. The briefing editor keeps
// stages as in-memory legacy JSONB entries keyed by a "day{index}_"
// name prefix rather than by event_date, because the producer can add
// and reorder days before anything is saved and there are no
// briefing_stages rows to key against yet. Blank labels are rows the
// producer added but never filled in — they exist in the array but are
// not a stage.
//
// Named and tested here because the same prefix filter is open-coded in
// several places in CreateEditBriefing.tsx and LiveTrackingSetup.tsx;
// this is the version new code should call.
export function wizardStagesForDay<T extends { name: string; label: string }>(
  stages: T[],
  dayIndex: number,
): T[] {
  const prefix = `day${dayIndex}_`;
  // `label` is typed non-null but arrives from JSONB, so it can be
  // missing on legacy rows — hence the truthiness check before trim().
  return stages.filter((stage) => stage.name.startsWith(prefix) && !!stage.label?.trim());
}

export type ScheduleStage = { name: string; label: string };

type ScheduleArtist = { stage_id?: string | null; stage_name?: string | null };

// Resolve the stages to render in the crew schedule for a single day.
//
// The crew schedule must show EVERY stage configured for the day —
// including stages that have no artists yet — so the producer can
// schedule crew against an empty stage. The authoritative per-day list
// is `dayStages` (the briefing_stages rows for that date, already
// ordered by display_order).
//
// As a safety net we also keep any stage that already holds a lineup
// today but has no briefing_stages row yet, so a schedule is never
// silently dropped. Artists match FK-first (stage_id, rename-stable)
// with a stage_name fallback. Result is deduped by label and ordered by
// display_order; orphan (lineup-only) stages sort last.
export function resolveScheduleStages(input: {
  dayStages: BriefingStageRecord[];
  allStages: ScheduleStage[];
  briefingStages: Pick<BriefingStageRecord, "id" | "label">[];
  artists: ScheduleArtist[];
}): ScheduleStage[] {
  const { dayStages, allStages, briefingStages, artists } = input;
  const displayOrderByLabel = new Map(dayStages.map((s) => [s.label, s.display_order]));

  const byLabel = new Map<string, ScheduleStage>();
  for (const s of dayStages) {
    byLabel.set(s.label, { name: s.label, label: s.label });
  }
  for (const stage of allStages) {
    const label = stage.label || stage.name;
    if (byLabel.has(label)) continue;
    const rec = briefingStages.find((s) => s.label === label);
    const hasArtists = artists.some((a) => {
      const aStageId = a.stage_id ?? null;
      if (rec && aStageId) return aStageId === rec.id;
      return a.stage_name === label;
    });
    if (hasArtists) byLabel.set(label, stage);
  }

  return Array.from(byLabel.values()).sort(
    (a, b) =>
      (displayOrderByLabel.get(a.label || a.name) ?? Number.MAX_SAFE_INTEGER) -
      (displayOrderByLabel.get(b.label || b.name) ?? Number.MAX_SAFE_INTEGER),
  );
}

// Stable bucket identifier for analytics rollups. Prefers the FK
// (`id:<uuid>`) so renaming a stage doesn't fork its analytics; falls
// back to the label for legacy rows that haven't been backfilled with
// a stage_id yet, and finally to "unassigned" for orphans.
export function stageBucketKey(stage_id: string | null, fallbackLabel: string | null): string {
  if (stage_id) return `id:${stage_id}`;
  if (fallbackLabel && fallbackLabel.length > 0) return `label:${fallbackLabel}`;
  return "unassigned";
}

// Per-day stage-id resolver. Same label can appear on multiple days
// post-001300 — a label-only lookup loses day-2's id whenever day-1
// had the same label. Saving with that wrong id corrupts lineup /
// crew_assignments FKs and breaks the post-save sync's FK fast-path
// (assignments end up stretched to the slot's full bounds because
// `lineup_id` couldn't be auto-filled). Always resolve with the
// row's event_date.
//
// Resolution rules:
//   1. Exact (label, event_date) match — preferred path.
//   2. Otherwise, label-only fallback ONLY when that label is unique
//      across all days. If the label appears on multiple days but
//      none on the requested event_date, we return `null` rather
//      than guessing a different day. The producer-reported "Final
//      test" corruption (2026-05-06) was the resolver picking day-3's
//      id for a day-1 row whose stage_name was day-3's label — the
//      fallback masked the input bug and persisted it through the
//      day-aware DB trigger as well.
export function buildStageIdResolver(
  rows: ReadonlyArray<{ id: string; label: string; event_date: string }>,
): (label: string, eventDate: string) => string | null {
  const byKey = new Map<string, string>();
  const idsByLabel = new Map<string, Set<string>>();
  const firstIdByLabel = new Map<string, string>();
  for (const r of rows) {
    byKey.set(`${r.label}::${r.event_date}`, r.id);
    let ids = idsByLabel.get(r.label);
    if (!ids) {
      ids = new Set();
      idsByLabel.set(r.label, ids);
    }
    ids.add(r.id);
    if (!firstIdByLabel.has(r.label)) firstIdByLabel.set(r.label, r.id);
  }
  return (label, eventDate) => {
    const exact = byKey.get(`${label}::${eventDate}`);
    if (exact) return exact;
    const ids = idsByLabel.get(label);
    // Only fall back to label-only when that label is unambiguous —
    // i.e. it maps to exactly one stage_id across the whole briefing.
    // Multi-day briefings with the same label per day must NOT fall
    // back: returning the wrong day's id would corrupt the FK.
    if (ids && ids.size === 1) return firstIdByLabel.get(label) ?? null;
    return null;
  };
}
